import React, { useEffect, useRef, useState } from "react";

import { InputNumber as AntInputNumber } from "antd";
import { NamePath } from "antd/es/form/interface";

import { parseInputRules, useTranslation } from "utils";

import { getUnitDisplayLabel } from "consts";

import { Flex, Form, GuidanceTextBox, InputLabel, InputNumberProps, Option, Select } from "components";

export type InputNumberWithUnitSelectionProps = Omit<InputNumberProps, "suffix" | "onChange"> & {
  onChange?: (value: number | null, unit: string | null) => void;
  units?: string[];
  unitLabel?: string;
  unitValue?: string | null;
  unitDisplayOverride?: Record<string, string>;
};

/**
 * Forces a naming convention for the unit selector's form field, mirroring `${dataTestId}-unit-selector`:
 * the unit field name always mirrors `name`, with its last path segment suffixed by "Unit".
 */
const getUnitFieldName = (name?: NamePath): NamePath | undefined => {
  if (name === undefined) return undefined;
  if (Array.isArray(name)) {
    if (name.length === 0) return undefined;
    return [...name.slice(0, -1), `${name[name.length - 1]}Unit`];
  }
  return `${name}Unit`;
};

export const InputNumberWithUnitSelector = ({
  className = "",
  labelClassName = "",
  decimalSeparator = ",",
  defaultValue,
  disabled,
  initialValue,
  unitValue,
  precision,
  label,
  tooltip,
  message,
  min,
  max,
  name,
  rules,
  validateFirst,
  step,
  value,
  dataTestId,
  units,
  unitLabel,
  unitDisplayOverride,
  guidanceText,
  help,
  validateStatus,
  onBlur = () => {},
  onChange = (_, __) => {},
}: InputNumberWithUnitSelectionProps) => {
  const { t } = useTranslation("misc");
  const inputElement = useRef<HTMLInputElement>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<number | null>(initialValue ?? null);
  const [internalUnit, setInternalUnit] = useState<string | null>(unitValue ?? null);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBeenFocused(false);
    onBlur(e);
  };

  const handleChangeNumber = (newValue: number | null) => {
    setInternalValue(newValue);
    onChange(newValue, internalUnit);
  };

  const handleChangeUnit = (newUnit: string) => {
    setInternalUnit(newUnit);
    onChange(value ?? internalValue ?? null, newUnit);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = !!(hasBeenFocused || defaultValue || initialValue || initialValue === 0 || internalValue || internalValue === 0);
  const required = rules && rules[0] && rules[0].required;

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value ?? null);
    }
  }, [value]);

  useEffect(() => {
    setInternalUnit(unitValue ?? null);
  }, [unitValue]);

  return (
    <div>
      <Form.Item
        className={`floating-input input-number-compound ${className} ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"}`}
        colon={false}
        label={<InputLabel active={active} required={required} label={label} className={labelClassName} handleClick={handleLabelClick} tooltip={tooltip} />}
        name={name}
        rules={parseInputRules(rules, message)}
        validateFirst={validateFirst ?? false}
        initialValue={initialValue || initialValue === 0 ? initialValue : defaultValue}
        help={help}
        validateStatus={validateStatus}
        normalize={(value) => {
          if (typeof value === "string" && value.trim() !== "") {
            const normalizedValue = value.replace(",", ".");
            const num = Number(normalizedValue);
            return Number.isNaN(num) ? value : num;
          }
          return value;
        }}
      >
        <Flex className={`input-inline-container ${hasBeenFocused ? "active" : ""}`}>
          <AntInputNumber
            className="number-input-with-unit-selection"
            data-testid={dataTestId}
            decimalSeparator={decimalSeparator || ","}
            disabled={disabled}
            min={min}
            max={max}
            precision={precision}
            ref={inputElement}
            step={step}
            value={value}
            onBlur={handleBlur}
            onChange={handleChangeNumber}
            onFocus={() => setHasBeenFocused(true)}
          />
          {units ? (
            <div className="unit-selector">
              {
                <Select
                  label={unitLabel ?? t("unit")}
                  dataTestId={`${dataTestId}-unit-selector`}
                  name={getUnitFieldName(name)}
                  initialValue={internalUnit}
                  onChange={handleChangeUnit}
                  value={internalUnit}
                  dropdownStyle={{ zIndex: 10010 }}
                  optionFilterProp="children"
                  disabled={disabled}
                >
                  {units.map((unit, index) => (
                    <Option data-testid={`${dataTestId}-unit-selector-option-${index}`} key={index} value={unit}>
                      {unitDisplayOverride?.[unit] ?? getUnitDisplayLabel(unit)}
                    </Option>
                  ))}
                </Select>
              }
            </div>
          ) : null}
        </Flex>
      </Form.Item>
      {guidanceText ? <GuidanceTextBox t={t} guidanceText={guidanceText} /> : null}
    </div>
  );
};
